import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addShoppingItem,
  deleteShoppingItem,
  generateShoppingList,
  getActiveShoppingList,
  patchShoppingItem,
} from '../api/shopping.js';
import type {
  AddItemRequest,
  GenerateRequest,
  ShoppingListDto,
  ShoppingListItemDto,
} from '../api/shoppingTypes.js';
import {
  loadMirror,
  mirrorList,
  useOfflineSync,
  type PendingMutation,
  type UseOfflineSyncResult,
} from './useOfflineSync.js';

export interface UseShoppingListResult {
  list: ShoppingListDto | null;
  loading: boolean;
  error: string | null;
  progress: { completed: number; total: number; pct: number };
  generate: (req: GenerateRequest) => Promise<void>;
  refresh: () => Promise<void>;
  toggle: (itemId: number, value: boolean) => Promise<void>;
  addItem: (req: AddItemRequest) => Promise<void>;
  removeItem: (itemId: number) => Promise<void>;
  reorderItem: (itemId: number, targetIndex: number) => Promise<void>;
  clearAll: () => Promise<void>;
  sync: UseOfflineSyncResult;
}

export function useShoppingList(): UseShoppingListResult {
  const [list, setList] = useState<ShoppingListDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const dto = await getActiveShoppingList();
      if (dto) {
        await mirrorList(dto);
      }
      setList(dto);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const sync = useOfflineSync(
    useCallback(async (m: PendingMutation): Promise<void> => {
      if (m.kind === 'toggle') {
        await patchShoppingItem(m.item_id, { is_checked: m.is_checked });
      } else {
        await addShoppingItem({
          list_id: m.payload.list_id,
          name: m.payload.name,
          amount: m.payload.amount,
          unit: m.payload.unit as AddItemRequest['unit'],
        });
      }
    }, []),
  );

  useEffect(() => {
    void (async () => {
      try {
        const dto = await getActiveShoppingList();
        if (dto) {
          setList(dto);
          await mirrorList(dto);
        } else {
          const cached = await loadMirror(-1);
          setList(cached);
        }
      } catch {
        const cached = await loadMirror(-1);
        setList(cached);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const generate = useCallback(
    async (req: GenerateRequest): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const result = await generateShoppingList(req);
        const dto: ShoppingListDto = {
          list_id: result.list_id,
          title: `Shopping ${req.start_date} – ${req.end_date}`,
          start_date: req.start_date,
          end_date: req.end_date,
          items: result.items,
          total: result.items.length,
          completed: result.items.filter((i) => i.is_checked).length,
        };
        setList(dto);
        await mirrorList(dto);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to generate';
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const applyToggle = useCallback(
    async (itemId: number, value: boolean): Promise<void> => {
      if (!list) return;
      const items: ShoppingListItemDto[] = list.items.map((it) =>
        it.id === itemId ? { ...it, is_checked: value } : it,
      );
      const completed = items.filter((i) => i.is_checked).length;
      const next: ShoppingListDto = { ...list, items, completed, total: items.length };
      setList(next);
      await mirrorList(next);
    },
    [list],
  );

  const toggle = useCallback(
    async (itemId: number, value: boolean): Promise<void> => {
      if (!list) return;
      await applyToggle(itemId, value);
      try {
        await patchShoppingItem(itemId, { is_checked: value });
      } catch {
        await sync.enqueue({ kind: 'toggle', item_id: itemId, is_checked: value });
      }
    },
    [applyToggle, list, sync],
  );

  const addItem = useCallback(
    async (req: AddItemRequest): Promise<void> => {
      if (!list) {
        await sync.enqueue({
          kind: 'add',
          payload: {
            list_id: req.list_id,
            name: req.name,
            amount: req.amount,
            unit: req.unit,
          },
        });
        return;
      }
      try {
        const created = await addShoppingItem(req);
        const items = [...list.items, { ...created, position: list.items.length }];
        const next: ShoppingListDto = {
          ...list,
          items,
          total: items.length,
          completed: items.filter((i) => i.is_checked).length,
        };
        setList(next);
        await mirrorList(next);
      } catch {
        await sync.enqueue({
          kind: 'add',
          payload: {
            list_id: req.list_id,
            name: req.name,
            amount: req.amount,
            unit: req.unit,
          },
        });
      }
    },
    [list, sync],
  );

  const removeItem = useCallback(
    async (itemId: number): Promise<void> => {
      if (!list) return;
      const removed = list.items.find((it) => it.id === itemId);
      if (!removed) return;
      const items = list.items.filter((it) => it.id !== itemId);
      const next: ShoppingListDto = {
        ...list,
        items,
        total: items.length,
        completed: items.filter((i) => i.is_checked).length,
      };
      setList(next);
      await mirrorList(next);
      try {
        await deleteShoppingItem(itemId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to remove item';
        setError(msg);
      }
      void removed;
    },
    [list],
  );

  const reorderItem = useCallback(
    async (itemId: number, targetIndex: number): Promise<void> => {
      if (!list) return;
      const items = [...list.items];
      const fromIdx = items.findIndex((it) => it.id === itemId);
      if (fromIdx < 0) return;
      const clamped = Math.max(0, Math.min(targetIndex, items.length - 1));
      if (clamped === fromIdx) return;
      const [moved] = items.splice(fromIdx, 1);
      items.splice(clamped, 0, moved!);
      const reindexed = items.map((it, idx) => ({ ...it, position: idx }));
      const next: ShoppingListDto = {
        ...list,
        items: reindexed,
        total: reindexed.length,
        completed: reindexed.filter((i) => i.is_checked).length,
      };
      setList(next);
      await mirrorList(next);
      try {
        await patchShoppingItem(itemId, { position: clamped });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to reorder item';
        setError(msg);
      }
    },
    [list],
  );

  const clearAll = useCallback(async (): Promise<void> => {
    if (!list) return;
    const items = list.items.map((it) => ({ ...it, is_checked: false }));
    const next: ShoppingListDto = { ...list, items, completed: 0, total: items.length };
    setList(next);
    await mirrorList(next);
    const targets = list.items.filter((i) => i.is_checked);
    for (const it of targets) {
      try {
        await patchShoppingItem(it.id, { is_checked: false });
      } catch {
        await sync.enqueue({ kind: 'toggle', item_id: it.id, is_checked: false });
      }
    }
  }, [list, sync]);

  const progress = useMemo(() => {
    const total = list?.items.length ?? 0;
    const completed = list?.items.filter((i) => i.is_checked).length ?? 0;
    const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
    return { completed, total, pct };
  }, [list]);

  return {
    list,
    loading,
    error,
    progress,
    generate,
    refresh,
    toggle,
    addItem,
    removeItem,
    reorderItem,
    clearAll,
    sync,
  };
}
